import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import TaapTabs from '../../components/taap/TaapTabs';
import { Menu } from 'lucide-react';
import metaConnectionService from '../../services/api/metaConnectionService';
import { MetaConnectionState, MetaOAuthCandidate } from '../../types/metaConnection';
import { reviewSafeText, useMetaLang, MC_STRINGS, toggleLabel, otherLang } from '../../i18n/metaReviewLang';
import { ResponsiveAppShell, TaskyButton, TaskyPageHeader } from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import GoogleAdsConnectionCard from '../../components/settings/GoogleAdsConnectionCard';
import { useConfirm } from '../../components/tasky/modal';

// ============================================================
// Tailwind class constants (旧 styled-components)
// ============================================================

const PAGE_CONTAINER = 'flex min-h-[calc(100dvh_-_56px)] bg-gray-50 lg:h-screen lg:min-h-0';

const MAIN_CONTENT = 'flex-1 overflow-y-auto overflow-x-hidden flex flex-col';

const CONTENT_AREA = 'p-6 max-w-[1400px] w-full mx-auto max-[640px]:p-4';

// 設定カードは読みやすい幅で止める（タブ行と本体の幅は別物）。
const SETTINGS_COLUMN = 'max-w-[880px]';

const CARD = 'bg-white border border-gray-200 rounded-xl p-6 mb-5 max-[640px]:p-4';

const CARD_HEADER = 'flex items-center justify-between mb-4 max-[640px]:items-start max-[640px]:flex-col max-[640px]:gap-3';

const PROVIDER_ROW = 'flex items-center gap-3';

const META_LOGO = 'w-10 h-10 rounded-[10px] bg-[#1877f2] text-white flex items-center justify-center font-extrabold text-[20px]';

const PROVIDER_NAME = 'text-[16px] font-bold text-gray-800';

const PROVIDER_META = 'text-[12px] text-gray-500';

const STATUS_BADGE_BASE = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold before:content-[''] before:w-2 before:h-2 before:rounded-full";
const STATUS_BADGE_ON = 'text-green-700 bg-green-100 before:bg-green-600';
const STATUS_BADGE_OFF = 'text-gray-500 bg-gray-100 before:bg-gray-400';

const INFO_GRID = 'grid grid-cols-[160px_1fr] gap-x-4 gap-y-[10px] text-[14px] mt-2 max-[640px]:grid-cols-[1fr] max-[640px]:gap-y-1.5';

const INFO_LABEL = 'text-gray-500';

const INFO_VALUE = 'text-gray-800 font-medium break-all';

const PERMISSION_LIST = 'flex flex-wrap gap-2';

const PERMISSION_CHIP_BASE = "inline-flex items-center gap-1.5 px-[10px] py-1 rounded-lg text-[12px] font-['SFMono-Regular',Menlo,monospace] border";
const PERMISSION_CHIP_GRANTED = 'text-blue-700 bg-blue-50 border-blue-200';
const PERMISSION_CHIP_DENIED = 'text-gray-400 bg-gray-100 border-gray-200';

const LABEL = 'block text-[14px] font-semibold text-gray-700 mb-2';

const TOKEN_TEXTAREA = "w-full min-h-24 px-[14px] py-3 border border-gray-300 rounded-[10px] text-[13px] font-['SFMono-Regular',Menlo,monospace] text-gray-800 resize-y focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30";

const PRIMARY_BUTTON = 'mt-4 px-6 py-3 border-0 rounded-[10px] bg-[#1877f2] text-white text-[15px] font-semibold cursor-pointer transition-[background] duration-150 ease-[ease] enabled:hover:bg-[#1366d6] disabled:opacity-60 disabled:cursor-not-allowed max-[640px]:w-full max-[640px]:min-h-[44px]';

const SECONDARY_BUTTON_BASE = 'px-4 py-2 border rounded-lg text-[13px] font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';
const SECONDARY_BUTTON = `${SECONDARY_BUTTON_BASE} border-gray-300 bg-white text-gray-700 enabled:hover:bg-gray-50`;
const DANGER_BUTTON = `${SECONDARY_BUTTON_BASE} border-red-200 bg-white text-red-600 enabled:hover:bg-red-50`;

// 接続済み広告アカウント 1 行（名前と、その行だけ外す解除ボタン）。
const ACCOUNT_ROW = 'flex items-start justify-between gap-3 py-1';

const ROW_DANGER_BUTTON = 'shrink-0 h-8 text-red-600 hover:text-red-700 hover:bg-red-50';

const BUTTON_ROW = 'flex gap-2 mt-5 max-[640px]:flex-col max-[640px]:[&_button]:w-full max-[640px]:[&_button]:min-h-[44px]';

const ALERT_BASE = 'mt-4 px-[14px] py-3 rounded-[10px] text-[13px] border';
const ALERT_ERROR = `${ALERT_BASE} text-red-700 bg-red-50 border-red-200`;
const ALERT_SUCCESS = `${ALERT_BASE} text-green-700 bg-green-50 border-green-200`;

const HELP_PANEL = 'bg-slate-50 border border-slate-200 rounded-[10px] p-4 text-[13px] leading-[1.7] text-slate-600 mt-4';

const HELP_TITLE = 'font-bold text-slate-700 mb-1.5';

const LOADING = 'p-12 text-center text-gray-400';

const OAUTH_PANEL = 'border border-blue-100 bg-[#f8fbff] rounded-[10px] p-4';

const OAUTH_BODY = 'mt-0 mx-0 mb-3 text-[13px] text-slate-600 leading-[1.7]';

const SCOPE_NOTE = 'mt-3 text-[12px] text-slate-500 leading-[1.6]';

const REAUTH_BANNER = 'mt-4 p-[14px] rounded-[10px] border border-orange-200 bg-orange-50 text-orange-800 text-[13px] leading-[1.7]';

const REAUTH_TITLE = 'font-bold mb-1';

const CANDIDATE_LIST = 'flex flex-col gap-2 my-3';

const CANDIDATE_ROW = 'flex items-center gap-2.5 p-3 border border-gray-200 rounded-[10px] cursor-pointer text-[14px] text-gray-800 hover:bg-gray-50';

const CANDIDATE_META = 'text-gray-400 text-[12px]';

const ADVANCED_TOGGLE = 'mt-4 p-0 border-none bg-transparent text-gray-500 text-[12px] underline cursor-pointer';

const WRITE_BADGE_BASE = 'inline-flex items-center px-2.5 py-[3px] rounded-lg text-[12px] font-semibold';
const WRITE_BADGE_ON = `${WRITE_BADGE_BASE} text-green-700 bg-green-100`;
const WRITE_BADGE_OFF = `${WRITE_BADGE_BASE} text-amber-700 bg-amber-100`;

// ============================================================
// Helpers
// ============================================================

function getErrorMessage(err: unknown, fallback: string): string {
  const anyErr = err as { response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || fallback;
}

// Scopes this integration requires: ads_read for the dashboard, ads_management
// for the user-approved PAUSED writes. business_management is never requested.
const KNOWN_PERMISSIONS = ['ads_read', 'ads_management'];

// UTC for the English UI used in Meta App Review; Japan time for everyone else.
function formatCheckedAt(iso: string | null | undefined, lang: 'ja' | 'en'): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  if (lang === 'en') return date.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  return date
    .toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .replace(/\//g, '-');
}

// ============================================================
// Component
// ============================================================

const MediaConnectPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lang, setLang] = useMetaLang();
  const t = MC_STRINGS[lang];

  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<MetaConnectionState | null>(null);
  const [token, setToken] = useState('');
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // OAuth account picker state (populated when Meta bounces back with
  // ?oauth=pending&state=...).
  const [candidates, setCandidates] = useState<MetaOAuthCandidate[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [oauthState, setOauthState] = useState<string | null>(null);
  const [candidatesCanManage, setCandidatesCanManage] = useState(true);
  const confirmDialog = useConfirm();

  const loadConnection = useCallback(async () => {
    setLoading(true);
    try {
      const data = await metaConnectionService.getConnection();
      setConnection(data);
    } catch (err) {
      console.error('Failed to load Meta connection:', err);
      setConnection({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  // Handle the redirect back from Meta. The callback stashed the token and the
  // /me/adaccounts list server-side; here we only pull the candidate list.
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const state = searchParams.get('state');

    if (oauth === 'error') {
      // Meta の callback はコードで返す（英語表示で日本語を出さないため）。他の経路は従来どおり message を出す
      const errorCode = searchParams.get('error_code');
      setError(errorCode ? (t.oauthErrors[errorCode] || t.errOAuthStart) : (searchParams.get('message') || t.oauthCancelled));
      setSearchParams({}, { replace: true });
      return;
    }
    if (oauth !== 'pending' || !state) return;

    setOauthState(state);
    metaConnectionService
      .getOAuthCandidates(state)
      .then((data) => {
        setCandidates(data.candidates);
        setCandidatesCanManage(data.can_manage_ads);
        setSelectedIds(data.candidates.length === 1 ? [data.candidates[0].id] : []);
      })
      .catch(() => setError(t.errOAuthCandidates))
      .finally(() => setSearchParams({}, { replace: true }));
    // t is derived from lang; re-running on language change would re-fetch a
    // consumed state, so key this on the query string only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleStartOAuth = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const url = await metaConnectionService.initiateOAuth();
      window.location.href = url;
    } catch (err) {
      setError(lang === 'en' ? t.errOAuthStart : getErrorMessage(err, t.errOAuthStart));
      setSubmitting(false);
    }
  };

  const handleConfirmAccounts = async () => {
    if (!oauthState) return;
    if (selectedIds.length === 0) {
      setError(t.pickerNoSelection);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await metaConnectionService.confirmOAuthAccounts(oauthState, selectedIds);
      setCandidates(null);
      setOauthState(null);
      setSuccess(t.connectSuccess);
      await loadConnection();
    } catch (err) {
      setError(lang === 'en' ? t.errOAuthConfirm : getErrorMessage(err, t.errOAuthConfirm));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCandidate = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleConnect = async () => {
    if (!token.trim()) {
      setError(t.errEmpty);
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await metaConnectionService.connect(token.trim());
      setConnection(data);
      setToken('');
      setSuccess(t.success);
    } catch (err) {
      // In English mode show the localized message (backend messages are Japanese).
      setError(lang === 'en' ? t.errConnect : getErrorMessage(err, t.errConnect));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await metaConnectionService.disconnect();
      await loadConnection();
    } catch (err) {
      setError(lang === 'en' ? t.errDisconnect : getErrorMessage(err, t.errDisconnect));
    } finally {
      setSubmitting(false);
    }
  };

  // 代理店が複数の広告アカウントを持つとき、1 件だけ外せるようにする。
  // Meta 側の許可取り消しは、同じ Meta ユーザーの最後の 1 件のときだけサーバーが行う。
  const handleDisconnectAccount = async (adAccountId: number) => {
    if (!(await confirmDialog({ title: t.confirmDisconnectOne, tone: 'destructive', confirmText: t.disconnectOne }))) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await metaConnectionService.disconnectAccount(adAccountId);
      setSuccess(t.disconnectOneSuccess);
      await loadConnection();
    } catch (err) {
      setError(lang === 'en' ? t.errDisconnect : getErrorMessage(err, t.errDisconnect));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isConnected = connection?.connected === true;
  const grantedPermissions = (connection?.permissions ?? [])
    .filter((p) => p.status === 'granted')
    .map((p) => p.permission);
  // Always show the key scopes Meta review cares about, marking grant status.
  const permissionDisplay = Array.from(new Set([...KNOWN_PERMISSIONS, ...grantedPermissions]));
  const requestedScopes = connection?.requested_scopes ?? KNOWN_PERMISSIONS;
  const accounts = connection?.accounts ?? [];
  // A connection is write-capable only when every selected account has
  // ads_management granted on the stored token.
  const canManageAds = accounts.length > 0 && accounts.every((a) => a.can_manage_ads === true);
  const needsReauth = isConnected && !canManageAds;
  // 別の Meta ログインで追加したアカウントが混ざるときだけ、接続ユーザー名を行に出す。
  const showConnectedBy = new Set(accounts.map((a) => a.meta_user_name).filter(Boolean)).size > 1;
  // The permission alone is not enough: writes are also switched off per
  // environment (META_ADS_WRITE_ENABLED), and production keeps them off.
  const canWriteAds = canManageAds && connection?.write_enabled === true;
  const authMethodLabel =
    connection?.auth_method === 'oauth' ? t.authMethodOAuth : t.authMethodToken;

  const oauthPanel = (
    <div className={OAUTH_PANEL}>
      <p className={OAUTH_BODY}>{t.oauthSectionBody}</p>
      <button className={PRIMARY_BUTTON} onClick={handleStartOAuth} disabled={submitting} style={{ marginTop: 0 }}>
        {submitting ? t.oauthRedirecting : t.oauthConnect}
      </button>
      <div className={SCOPE_NOTE}>
        {t.oauthScopesLabel}: {requestedScopes.join(', ')}
        <br />
        {t.oauthNoBusinessManagement}
      </div>
    </div>
  );

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      onToggleCollapse={(c) => setSidebarCollapsed(c)}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = <Sidebar onLogout={handleLogout} />;

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle={t.pageTitle}
      topbarActions={
        <>
          <button className={SECONDARY_BUTTON} onClick={() => setLang(otherLang(lang))} title="Language">
            {toggleLabel(lang)}
          </button>
        </>
      }
    >
    <div className={PAGE_CONTAINER}>
      <div className={MAIN_CONTENT}>
        <TaskyPageHeader
          className="hidden lg:block"
          title={
            <span className="inline-flex items-center gap-3">
              {sidebarCollapsed && (
                <TaskyButton
                  variant="ghost"
                  size="icon"
                  aria-label={lang === 'ja' ? 'サイドバーを展開' : 'Expand sidebar'}
                  onClick={() => setSidebarCollapsed(false)}
                  className="h-8 w-8"
                >
                  <Menu />
                </TaskyButton>
              )}
              {t.pageTitle}
            </span>
          }
          actions={
            <>
              <button className={SECONDARY_BUTTON} onClick={() => setLang(otherLang(lang))} title="Language">
                🌐 {toggleLabel(lang)}
              </button>
              <TaskyHeaderActions />
            </>
          }
        />

        <div className={CONTENT_AREA}>
          <div className="mb-5" data-tutorial="media-connect-tabs"><TaapTabs active="connections" /></div>
          <div className={SETTINGS_COLUMN}>
          <GoogleAdsConnectionCard />
          {loading ? (
            <div className={LOADING}>{t.loading}</div>
          ) : (
            <div className={CARD} data-tutorial="media-connect-meta">
              <div className={CARD_HEADER}>
                <div className={PROVIDER_ROW}>
                  <div className={META_LOGO}>f</div>
                  <div>
                    <div className={PROVIDER_NAME}>{t.providerName}</div>
                    <div className={PROVIDER_META}>
                      Marketing API {connection?.api_version || 'v21.0'}
                      {t.providerMetaSuffix}
                    </div>
                  </div>
                </div>
                <span className={`${STATUS_BADGE_BASE} ${isConnected ? STATUS_BADGE_ON : STATUS_BADGE_OFF}`}>
                  {isConnected ? t.connected : t.notConnected}
                </span>
              </div>

              {candidates !== null ? (
                /* Step 2 of the OAuth flow: pick which ad accounts to use. */
                <>
                  <div className={HELP_TITLE}>{t.pickerTitle}</div>
                  <p className={OAUTH_BODY}>{t.pickerBody}</p>

                  {!candidatesCanManage && (
                    <div className={REAUTH_BANNER}>
                      <div className={REAUTH_TITLE}>{t.reauthTitle}</div>
                      {t.reauthBody}
                    </div>
                  )}

                  {candidates.length === 0 ? (
                    <div className={ALERT_ERROR}>{t.pickerEmpty}</div>
                  ) : (
                    <div className={CANDIDATE_LIST}>
                      {candidates.map((c) => (
                        <label className={CANDIDATE_ROW} key={c.id}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(c.id)}
                            onChange={() => toggleCandidate(c.id)}
                          />
                          <span>{reviewSafeText(c.name, lang, c.id)}</span>
                          <span className={CANDIDATE_META}>({c.id})</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {error && <div className={ALERT_ERROR}>{error}</div>}

                  <div className={BUTTON_ROW}>
                    <button className={PRIMARY_BUTTON}
                      onClick={handleConfirmAccounts}
                      disabled={submitting || candidates.length === 0}
                      style={{ marginTop: 0 }}
                    >
                      {submitting ? t.pickerConfirming : t.pickerConfirm}
                    </button>
                    <button className={SECONDARY_BUTTON}
                      onClick={() => {
                        setCandidates(null);
                        setOauthState(null);
                      }}
                      disabled={submitting}
                    >
                      {t.pickerCancel}
                    </button>
                  </div>
                </>
              ) : isConnected ? (
                <>
                  <div className={INFO_GRID}>
                    <div className={INFO_LABEL}>{t.account}</div>
                    <div className={INFO_VALUE}>{reviewSafeText(connection?.meta_user?.name, lang, 'Meta account')}</div>

                    <div className={INFO_LABEL}>{t.authMethod}</div>
                    <div className={INFO_VALUE}>{authMethodLabel}</div>

                    <div className={INFO_LABEL}>{t.adAccounts}</div>
                    <div className={INFO_VALUE}>
                      {accounts.map((a) => (
                        <div key={a.platform_account_id} className={ACCOUNT_ROW} data-testid="meta-connected-account">
                          <div>
                            {reviewSafeText(a.account_name, lang, a.platform_account_id)}
                            <span style={{ color: '#9ca3af' }}>
                              {' '}
                              ({a.account_id_suffix || a.platform_account_id})
                            </span>
                            {a.is_test_account && <span className={CANDIDATE_META}> · {t.testAccount}</span>}
                            {showConnectedBy && a.meta_user_name && (
                              <span className={CANDIDATE_META}> · {t.connectedBy}: {reviewSafeText(a.meta_user_name, lang, 'Meta user')}</span>
                            )}
                          </div>
                          {accounts.length > 1 && (
                            <TaskyButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              className={ROW_DANGER_BUTTON}
                              onClick={() => handleDisconnectAccount(a.id)}
                              disabled={submitting}
                            >
                              {t.disconnectOne}
                            </TaskyButton>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className={INFO_LABEL}>{t.permissions}</div>
                    <div className={INFO_VALUE}>
                      <div className={PERMISSION_LIST}>
                        {permissionDisplay.map((perm) => {
                          const granted = grantedPermissions.includes(perm);
                          return (
                            <span
                              key={perm}
                              className={`${PERMISSION_CHIP_BASE} ${granted ? PERMISSION_CHIP_GRANTED : PERMISSION_CHIP_DENIED}`}
                            >
                              {granted ? '✓' : '−'} {perm}
                            </span>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <span className={canWriteAds ? WRITE_BADGE_ON : WRITE_BADGE_OFF}>
                          {canWriteAds ? t.writeEnabled : t.writeDisabled}
                        </span>
                      </div>
                    </div>

                    <div className={INFO_LABEL}>{t.lastChecked}</div>
                    <div className={INFO_VALUE}>{formatCheckedAt(accounts[0]?.permissions_checked_at, lang)}</div>
                  </div>

                  {needsReauth && (
                    <div className={REAUTH_BANNER}>
                      <div className={REAUTH_TITLE}>{t.reauthTitle}</div>
                      {t.reauthBody}
                      <div style={{ marginTop: 10 }}>
                        <button
                          className={PRIMARY_BUTTON}
                          onClick={handleStartOAuth}
                          disabled={submitting}
                          style={{ marginTop: 0 }}
                        >
                          {submitting ? t.oauthRedirecting : t.reauthCta}
                        </button>
                      </div>
                    </div>
                  )}

                  {success && <div className={ALERT_SUCCESS}>{success}</div>}
                  {error && <div className={ALERT_ERROR}>{error}</div>}

                  <div className={BUTTON_ROW}>
                    <button className={SECONDARY_BUTTON} onClick={() => navigate('/ad-dashboard/meta')}>
                      {t.openDashboard}
                    </button>
                    {/* 接続済みでも別の Meta ログインで追加できる。確定は既存行を残して追記する */}
                    <TaskyButton variant="ghost" size="sm" className={SECONDARY_BUTTON} onClick={handleStartOAuth} disabled={submitting}>
                      {submitting ? t.processing : t.addAccount}
                    </TaskyButton>
                    <button className={DANGER_BUTTON} onClick={handleDisconnect} disabled={submitting}>
                      {submitting ? t.processing : accounts.length > 1 ? t.disconnectAll : t.disconnect}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* OAuth is the primary path; the token form stays available
                      for the existing admin/ads_read workflow. */}
                  {oauthPanel}

                  {error && <div className={ALERT_ERROR}>{error}</div>}
                  {success && <div className={ALERT_SUCCESS}>{success}</div>}

                  <button type="button" className={ADVANCED_TOGGLE} onClick={() => setShowTokenForm((v) => !v)}>
                    {showTokenForm ? t.advancedTokenToggleHide : t.advancedTokenToggleShow}
                  </button>

                  {showTokenForm && (
                    <div style={{ marginTop: 16 }}>
                      <label className={LABEL} htmlFor="meta-token">{t.tokenLabel}</label>
                      <textarea
                        className={TOKEN_TEXTAREA}
                        id="meta-token"
                        placeholder={t.tokenPlaceholder}
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        spellCheck={false}
                      />

                      <button className={PRIMARY_BUTTON} onClick={handleConnect} disabled={submitting}>
                        {submitting ? t.connecting : t.connect}
                      </button>

                      <div className={HELP_PANEL}>
                        <div className={HELP_TITLE}>{t.helpTitle}</div>
                        {t.helpBody}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
    </ResponsiveAppShell>
  );
};

export default MediaConnectPage;
